import React, { memo } from 'react';

// Reusable Icon component for consistency
const CheckCircleIcon = () => <svg className="h-6 w-6 flex-shrink-0 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-13">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-14" />
    </svg>;

// --- NEW: X-Circle Icon for "Failed Solutions" ---
const XCircleIcon = () => <svg className="h-6 w-6 flex-shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-15">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-16" />
    </svg>;
export const ArticleContent = () => {
  return <div className="prose max-w-none space-y-6" data-id="element-17">
            <p className="font-medium text-lg" data-id="element-18">
                A groundbreaking discovery reveals why you feel exhausted despite
                adequate sleep—and the natural solution that could change everything.
            </p>
            <p data-id="element-19">At 2:47 AM on March 15th, Dr. Sarah Chen couldn't sleep. Again.</p>
            <p data-id="element-20">
                The Harvard Medical School researcher had just finished analyzing data
                that would change everything we thought we knew about chronic fatigue,
                brain fog, and why 77% of adults feel "mentally drained" despite getting
                adequate sleep.
            </p>

            {/* --- NEW: Authority Block --- */}
            <div className="not-prose my-12 flex flex-col sm:flex-row items-center gap-6 bg-gray-50 rounded-xl p-6 border border-gray-200" data-id="element-21">
                <img src="https://cdn.shopify.com/s/files/1/0637/1675/3506/files/234339_1363904.jpg.1500x1000_q95_crop-smart_upscale.jpg?v=1752095474" alt="Dr. Sarah Chen" className="w-24 h-24 rounded-full object-cover shadow-md" data-id="element-22" />
                <div className="text-center sm:text-left" data-id="element-23">
                    <blockquote className="text-lg font-medium text-gray-800 italic m-0 p-0" data-id="element-24">
                        "For the first time, we can see the true biological source of this exhaustion. It's not a character flaw—it's a cellular problem. And it has a solution."
                    </blockquote>
                    <p className="text-sm text-gray-500 mt-2" data-id="element-25">- Dr. Sarah Chen, Lead Researcher</p>
                </div>
            </div>

            <p data-id="element-26">
                Her findings scientifically explained the true source of the silent epidemic of exhaustion so many of us feel.
            </p>
            <p data-id="element-27">Then what Dr. Chen discovered will change everything.</p>
            <p data-id="element-28">
                Because the real reason you feel mentally drained has nothing to do with
                stress, poor sleep, or even your demanding lifestyle. The culprit is
                something far more sinister—and the solution has been deliberately
                hidden from you for decades.
            </p>
            <h2 className="text-2xl font-bold mt-10 mb-4" data-id="element-29">
                The Silent Epidemic Destroying America's Mind
            </h2>

            {/* --- NEW: Emphasized Pull Quote --- */}
            <blockquote className="not-prose text-center my-16 p-4 text-2xl md:text-3xl font-medium italic text-gray-600 border-l-4 border-r-4 border-rose-300" data-id="element-30">
                "The tired that sits in your bones. The kind that doesn't go away even after you rest..."
            </blockquote>

            <p data-id="element-31">If this sounds familiar, you've probably tried everything to fix it:</p>

            {/* --- NEW: "Failed Solutions" Section --- */}
            <div className="not-prose my-12 space-y-4" data-id="element-32">
                <div className="bg-red-50 p-4 rounded-lg flex items-center border border-red-200" data-id="element-33"><XCircleIcon data-id="element-34" /><p className="ml-3 font-semibold text-gray-700" data-id="element-35">Endless cups of coffee that just lead to jitters and a crash.</p></div>
                <div className="bg-red-50 p-4 rounded-lg flex items-center border border-red-200" data-id="element-36"><XCircleIcon data-id="element-37" /><p className="ml-3 font-semibold text-gray-700" data-id="element-38">Sugary energy drinks that make your heart race and hands shake.</p></div>
                <div className="bg-red-50 p-4 rounded-lg flex items-center border border-red-200" data-id="element-39"><XCircleIcon data-id="element-40" /><p className="ml-3 font-semibold text-gray-700" data-id="element-41">Expensive, ineffective supplements that taste awful and do nothing.</p></div>
            </div>

            <p data-id="element-42">And still... nothing. You still feel awful every day.</p>
            <p data-id="element-43">
                Worse, you feel the weight of letting people down. The guilt of not
                being the partner, parent, or professional you know you should be.
            </p>
            <p data-id="element-44">
                But Dr. Chen's research proves you're wrong. This isn't your fault. And
                it's definitely not normal.
            </p>
            <h2 className="text-2xl font-bold mt-10 mb-4" data-id="element-45">
                The $200 Billion Cover-Up
            </h2>
            <p data-id="element-46">
                What Dr. Chen discovered should have been front-page news. Instead, it
                was buried.
            </p>
            <div className="flex justify-center my-8" data-id="element-47">
                <img src="https://cdn.shopify.com/s/files/1/0637/1675/3506/files/20170925-lab-scientist-research-shutterstock-34on23mgbthkdbyruzbj0g.jpg?v=1752078216" alt="Laboratory research equipment" className="w-full max-w-2xl rounded-lg shadow-sm object-cover aspect-video" data-id="element-48" />
            </div>
            <p data-id="element-49">
                Why? Because her research exposed something the pharmaceutical industry
                desperately wants to keep secret.
            </p>
            <p data-id="element-50">
                Think about it: If you knew the real cause of your brain fog and chronic
                exhaustion could be fixed naturally and safely, would you still buy
                their billion-dollar "solutions" that only mask symptoms?
            </p>
            <p data-id="element-51">
                Of course not. And that's exactly why this information is suppressed.
            </p>
            <p data-id="element-52">
                Here's the dirty little secret they don't want you to know: The FDA
                doesn't actually require supplement companies to prove their products
                work BEFORE selling them. This creates a perfect cover for an industry
                to sell you ineffective, overpriced pills while profiting from your
                continued suffering. They can't patent ancient wisdom, so they ignore
                it.
            </p>
            <p data-id="element-53">So what is the fundamental principle they are so desperate to hide?</p>
            <p data-id="element-54">
                It's not some new 'miracle molecule' they can patent. It's a profound
                understanding of how the human body actually generates energy at a core
                level—an understanding that was sidelined by the modern "pill for every
                ill" approach.
            </p>
            <div className="flex justify-center my-8" data-id="element-55">
                <img src="https://cdn.shopify.com/s/files/1/0637/1675/3506/files/ChatGPT_Image_Jul_9_2025_01_03_01_PM.png?v=1752080589" alt="Traditional herbal medicine ingredients" className="w-full max-w-2xl rounded-lg shadow-sm object-cover aspect-video" data-id="element-56" />
            </div>
            <p data-id="element-57">
                For centuries, practitioners of Traditional Chinese Medicine and
                Ayurveda built their entire systems around a simple observation: a
                person's vitality, mood, and mental clarity were directly linked to
                their digestion and core energy.
            </p>
            <p data-id="element-58">
                They didn't use terms like "Gut-Brain Axis" or "Mitochondrial Function,"
                but they operated on the same core principle. This is the foundational
                wisdom that gets ignored because it's holistic, not synthetic.
            </p>
            <h2 className="text-2xl font-bold mt-10 mb-4" data-id="element-59">
                The Breakthrough That Vindicates This Principle
            </h2>
            <p data-id="element-60">
                Dr. Chen's breakthrough wasn't inventing a new chemical. It was using
                cutting-edge Harvard science to finally prove and quantify the principle
                that these traditions observed for millennia.
            </p>
            <p data-id="element-61">
                Her research focused on the two systems that control your body's energy
                and clarity at the most fundamental level:
            </p>
            {/* --- START: CORRECTED TWO-COLUMN SCIENCE SECTION --- */}
            <div className="not-prose my-12 space-y-12" data-id="element-62">
                {/* Gut-Brain Axis Section */}
                <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-63">
                    <div className="md:w-1/2 p-6" data-id="element-64">
                        <h3 className="text-xl font-bold mb-4 text-blue-800" data-id="element-65">
                            The Gut-Brain Communication Highway
                        </h3>
                        <p className="text-gray-700" data-id="element-66">
                            Her work confirmed that your gut is your "second brain." When the
                            communication line is broken, your entire system experiences brain
                            fog and fatigue. The vagus nerve serves as the primary pathway
                            connecting these two vital systems, and when it's compromised,
                            cognitive function rapidly deteriorates.
                        </p>
                    </div>
                    <div className="md:w-1/2" data-id="element-67">
                        <img src="https://cdn.shopify.com/s/files/1/0637/1675/3506/files/ChatGPT_Image_Jul_9_2025_01_09_48_PM.png?v=1752081012" alt="Abstract illustration of neural connections representing the gut-brain axis" className="rounded-lg shadow-lg object-cover w-full" data-id="element-68" />
                    </div>
                </div>
                {/* Mitochondria Section */}
                <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-69">
                    <div className="md:w-1/2 md:order-last p-6" data-id="element-70">
                        <h3 className="text-xl font-bold mb-4 text-green-800" data-id="element-71">
                            Your Cellular Power Plants (Mitochondria)
                        </h3>
                        <p className="text-gray-700" data-id="element-72">
                            She showed how modern life damages these tiny "power factories,"
                            crippling your body's ability to produce energy (ATP) at the
                            cellular level. When mitochondria are compromised, every system in
                            your body operates at a fraction of its potential, resulting in
                            the pervasive fatigue that characterizes modern life.
                        </p>
                    </div>
                    <div className="md:w-1/2" data-id="element-73">
                        <img src="https://cdn.shopify.com/s/files/1/0637/1675/3506/files/ChatGPT_Image_Jul_9_2025_01_14_19_PM.png?v=1752081267" alt="Abstract illustration of glowing cellular structures representing mitochondria" className="rounded-lg shadow-lg object-cover w-full" data-id="element-74" />
                    </div>
                </div>
            </div>
            {/* --- END: CORRECTED TWO-COLUMN SCIENCE SECTION --- */}
            <h2 className="text-2xl font-bold mt-10 mb-4" data-id="element-75">The Solution is Clarity</h2>
            <p data-id="element-76">
                Dr. Chen knew a brilliant protocol is useless if people can't stick with
                it. Her research, therefore, led to the development of Clarity—a
                revolutionary approach that packs the complete protocol into a single,
                delicious daily gummy.
            </p>
            <div className="flex justify-center my-8" data-id="element-77">
                <img src="https://cdn.shopify.com/s/files/1/0637/1675/3506/files/ChatGPT_Image_Jul_9_2025_01_23_07_PM.png?v=1752081793" alt="Clarity supplement bag and gummies" className="w-full max-w-lg rounded-lg shadow-sm" data-id="element-78" />
            </div>

            {/* --- NEW: "AS SEEN ON" TRUST BAR --- */}
            <div className="not-prose my-12 text-center" data-id="element-79">
                <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4" data-id="element-80">AS FEATURED IN</p>
                <div className="flex justify-center items-center space-x-6 sm:space-x-10" data-id="element-81">
                    <span className="font-serif font-bold text-xl text-gray-400" data-id="element-82">Well+Good</span>
                    <span className="font-serif font-bold text-xl text-gray-400" data-id="element-83">MindBodyGreen</span>
                    <span className="font-serif font-bold text-xl text-gray-400" data-id="element-84">Healthline</span>
                </div>
            </div>

            <p data-id="element-85">
                The unique gummy format is key. The "digestive priming" that occurs when
                you chew can increase nutrient absorption by up to 40% compared to
                conventional pills.
            </p>
            
            {/* --- NEW: "WHAT MAKES CLARITY DIFFERENT?" GRID --- */}
            <div className="not-prose my-16" data-id="element-86">
                <div className="bg-gray-50 rounded-2xl p-8 sm:p-12" data-id="element-87">
                    <h3 className="text-2xl font-bold text-center mb-8" data-id="element-88">What Makes Clarity Different?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-id="element-89">
                        <div className="flex items-start" data-id="element-90"><CheckCircleIcon data-id="element-91" /><p className="ml-3" data-id="element-92">Targets the <span className="font-semibold" data-id="element-93">root cause</span>, not just symptoms.</p></div>
                        <div className="flex items-start" data-id="element-94"><CheckCircleIcon data-id="element-95" /><p className="ml-3" data-id="element-96">Delicious taste you'll <span className="font-semibold" data-id="element-97">actually enjoy</span>.</p></div>
                        <div className="flex items-start" data-id="element-98"><CheckCircleIcon data-id="element-99" /><p className="ml-3" data-id="element-100">One simple gummy replaces <span className="font-semibold" data-id="element-101">handfuls of pills</span>.</p></div>
                        <div className="flex items-start" data-id="element-102"><CheckCircleIcon data-id="element-103" /><p className="ml-3" data-id="element-104">Maximum absorption with <span className="font-semibold" data-id="element-105">bioavailable ingredients</span>.</p></div>
                        <div className="flex items-start" data-id="element-106"><CheckCircleIcon data-id="element-107" /><p className="ml-3" data-id="element-108">No synthetic fillers or <span className="font-semibold" data-id="element-109">artificial junk</span>.</p></div>
                        <div className="flex items-start" data-id="element-110"><CheckCircleIcon data-id="element-111" /><p className="ml-3" data-id="element-112">Formulated based on <span className="font-semibold" data-id="element-113">cutting-edge research</span>.</p></div>
                    </div>
                </div>
            </div>
        </div>;
};
export default memo(ArticleContent);